import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const postsPerPage = 6;

  const carouselItems = [
    {
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&h=600&fit=crop",
      title: "Kenya Democracy and Leadership Project",
      subtitle: "Research-Driven Political Analysis",
      description: "Comprehensive research and strategic analysis on democratic processes, leadership development, and political innovation in Kenya.",
      cta: "Explore Research"
    },
    {
      image: "https://images.unsplash.com/photo-1486312338219-ce68b2c6b4f2?w=1200&h=600&fit=crop",
      title: "Strategic Political Insights",
      subtitle: "Evidence-Based Campaign Intelligence",
      description: "Stay ahead with cutting-edge research, strategic analysis, and political market intelligence from KISS experts.",
      cta: "Read Latest Insights"
    },
    {
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&h=600&fit=crop",
      title: "Democratic Leadership Studies",
      subtitle: "Leadership Development Research",
      description: "In-depth analysis of democratic leadership principles, governance structures, and political leadership development.",
      cta: "View Leadership Research"
    }
  ];

  useEffect(() => {
    if (!isHovered) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
      }, 6000);
      return () => clearInterval(timer);
    }
  }, [isHovered, carouselItems.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselItems.length) % carouselItems.length);
  };

  const blogPosts = [
    {
      id: 1,
      title: "Strategic Leadership in Kenya's Democratic Transition",
      excerpt: "Analysis of leadership qualities essential for navigating Kenya's complex political landscape and driving democratic development.",
      content: "Effective leadership in Kenya's democracy requires a unique blend of strategic thinking, cultural understanding, and institutional knowledge...",
      author: "Dr. Sarah Wanjiku",
      date: "2024-01-15",
      category: "leadership",
      readTime: "12 min read",
      icon: "bi-person-check",
      featured: true,
      tags: ["Strategic Leadership", "Democratic Development", "Political Analysis"]
    },
    {
      id: 2,
      title: "Institutional Strengthening and Democratic Governance",
      excerpt: "Research on mechanisms for strengthening democratic institutions and improving governance effectiveness across Kenya's 47 counties.",
      content: "Strong democratic institutions are the foundation of sustainable development. Our research examines the institutional frameworks...",
      author: "Prof. James Kiprop",
      date: "2024-01-10",
      category: "institutions",
      readTime: "15 min read",
      image: "🏛️",
      featured: true,
      tags: ["Democratic Institutions", "Governance", "Institutional Reform"]
    },
    {
      id: 3,
      title: "Electoral Systems and Democratic Representation",
      excerpt: "Comparative analysis of electoral systems and their impact on democratic representation and political accountability in Kenya.",
      content: "The choice of electoral system profoundly influences democratic representation and political behavior. Our research explores...",
      author: "Hon. Grace Achieng",
      date: "2024-01-08",
      category: "electoral-systems",
      readTime: "18 min read",
      icon: "bi-diagram-3",
      featured: false,
      tags: ["Electoral Systems", "Representation", "Democratic Processes"]
    },
    {
      id: 4,
      title: "Policy Development and Democratic Accountability",
      excerpt: "Research on effective policy development processes that ensure democratic accountability and responsive governance.",
      content: "Effective policy development requires systematic approaches that incorporate public input, expert analysis, and democratic accountability...",
      author: "Dr. David Oduya",
      date: "2024-01-05",
      category: "policy",
      readTime: "16 min read",
      icon: "bi-file-earmark-text",
      featured: false,
      tags: ["Policy Development", "Democratic Accountability", "Governance"]
    },
    {
      id: 5,
      title: "Political Communication and Public Discourse",
      excerpt: "Analysis of political communication strategies and their role in shaping public discourse and democratic participation.",
      content: "Effective political communication is essential for democratic participation and informed public discourse...",
      author: "Dr. Sarah Wanjiku",
      date: "2024-01-03",
      category: "communication",
      readTime: "14 min read",
      icon: "bi-chat-quote",
      featured: false,
      tags: ["Political Communication", "Public Discourse", "Media"]
    },
    {
      id: 6,
      title: "Youth Leadership and Democratic Participation",
      excerpt: "Research on youth engagement in democratic processes and strategies for developing the next generation of political leaders.",
      content: "Kenya's democratic future depends on active youth participation and leadership development...",
      author: "Hon. Grace Achieng",
      date: "2024-01-01",
      category: "youth",
      readTime: "13 min read",
      icon: "bi-people",
      featured: false,
      tags: ["Youth Leadership", "Democratic Participation", "Leadership Development"]
    },
    {
      id: 7,
      title: "Constitutional Governance and Institutional Reform",
      excerpt: "Analysis of constitutional governance mechanisms and recommendations for institutional reforms to strengthen democracy.",
      content: "Strong constitutional governance provides the framework for effective democratic institutions...",
      author: "Prof. James Kiprop",
      date: "2023-12-28",
      category: "constitution",
      readTime: "20 min read",
      image: "⚖️",
      featured: false,
      tags: ["Constitutional Governance", "Institutional Reform", "Rule of Law"]
    },
    {
      id: 8,
      title: "Election Day Operations: From Planning to Victory",
      excerpt: "Comprehensive guide to election day logistics, observer management, and real-time results monitoring for electoral success.",
      content: "Election day is the culmination of months of preparation. Success requires meticulous planning, trained personnel, and real-time adaptability...",
      author: "David Oduya",
      date: "2023-12-25",
      category: "operations",
      readTime: "14 min read",
      icon: "bi-check-circle",
      featured: false,
      tags: ["Election Day", "Operations", "Logistics"]
    }
  ];

  const categories = [
    { id: 'all', label: 'All Research', count: blogPosts.length },
    { id: 'leadership', label: 'Leadership', count: blogPosts.filter(p => p.category === 'leadership').length },
    { id: 'institutions', label: 'Institutions', count: blogPosts.filter(p => p.category === 'institutions').length },
    { id: 'electoral-systems', label: 'Electoral Systems', count: blogPosts.filter(p => p.category === 'electoral-systems').length },
    { id: 'policy', label: 'Policy', count: blogPosts.filter(p => p.category === 'policy').length },
    { id: 'communication', label: 'Communication', count: blogPosts.filter(p => p.category === 'communication').length },
    { id: 'youth', label: 'Youth', count: blogPosts.filter(p => p.category === 'youth').length },
    { id: 'constitution', label: 'Constitution', count: blogPosts.filter(p => p.category === 'constitution').length }
  ];

  const filteredPosts = activeCategory === 'all'
    ? blogPosts
    : blogPosts.filter(post => post.category === activeCategory);

  const featuredPosts = blogPosts.filter(post => post.featured);
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div>
      {/* Interactive Hero Carousel */}
      <section
        className="relative w-full overflow-hidden rounded-b-2xl shadow-2xl"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          className="flex transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {carouselItems.map((item, index) => (
            <div key={index} className="w-full flex-shrink-0">
              <div className="relative h-96 md:h-[500px] bg-gradient-to-br from-primary-700 to-primary-900">
                <div className="absolute inset-0 bg-black bg-opacity-50"></div>
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-white max-w-4xl px-4">
                    <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-2 text-shadow">
                      {item.title}
                    </h1>
                    <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-4 text-primary-200">
                      {item.subtitle}
                    </h2>
                    <p className="text-lg md:text-xl mb-8 text-secondary-100 leading-relaxed">
                      {item.description}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Link
                        to="#featured"
                        className="btn-primary bg-white text-primary-600 hover:bg-secondary-50 text-lg py-3 px-8 inline-block transform hover:scale-105 transition-all duration-300"
                      >
                        Featured Research
                      </Link>
                      <Link
                        to="#newsletter"
                        className="btn-secondary bg-transparent border-white text-white hover:bg-white hover:text-primary-600 text-lg py-3 px-8 inline-block transform hover:scale-105 transition-all duration-300"
                      >
                        Subscribe to Updates
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300 z-10"
          aria-label="Previous slide"
        >
          <i className="bi bi-chevron-left text-xl"></i>
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300 z-10"
          aria-label="Next slide"
        >
          <i className="bi bi-chevron-right text-xl"></i>
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
          {carouselItems.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-white scale-125'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Featured Posts */}
      <section id="featured" className="bg-white section-padding">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-4">
              Featured Insights
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              Essential reading for political strategists, campaign managers, and political leaders
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {featuredPosts.map((post) => (
              <article
                key={post.id}
                className="bg-gradient-to-br from-secondary-50 to-primary-50 p-8 rounded-3xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="text-6xl text-primary-600">
                    <i className={post.icon}></i>
                  </div>
                  <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    Featured
                  </span>
                </div>

                <div className="mb-4">
                  {post.tags.map((tag, index) => (
                    <span key={index} className="inline-block bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-xs mr-2 mb-2">
                      {tag}
                    </span>
                  ))}
                </div>

                <h3 className="text-2xl font-bold text-secondary-900 mb-4 leading-tight">
                  {post.title}
                </h3>

                <p className="text-secondary-600 mb-6 leading-relaxed">
                  {post.excerpt}
                </p>

                <div className="flex items-center justify-between text-sm text-secondary-500 mb-6">
                  <span>By {post.author}</span>
                  <span>{post.readTime}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-secondary-400 text-sm">
                    {new Date(post.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                  <button className="text-primary-600 font-semibold hover:text-primary-700 transition-colors duration-200">
                    Read More →
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-secondary-50 section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-4">
              Campaign Intelligence Hub
            </h2>
            <p className="text-xl text-secondary-600">
              Browse our comprehensive collection of political strategy insights and campaign analysis
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => {
                  setActiveCategory(category.id);
                  setCurrentPage(1);
                }}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform ${
                  activeCategory === category.id
                    ? 'bg-primary-600 text-white shadow-lg scale-105'
                    : 'bg-white text-secondary-700 hover:bg-primary-100 hover:scale-105'
                }`}
              >
                {category.label} ({category.count})
              </button>
            ))}
          </div>

          {/* Blog Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {currentPosts.map((post) => (
              <article
                key={post.id}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Featured Image */}
                <div className="w-full h-32 mb-4 rounded-lg overflow-hidden">
                  <img
                    src={
                      post.title.includes("Strategic Leadership")
                        ? "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=300&h=200&fit=crop"
                        : post.title.includes("Institutional Strengthening")
                        ? "https://images.unsplash.com/photo-1589391886645-d51941baf7fb?w=300&h=200&fit=crop"
                        : post.title.includes("Electoral Systems")
                        ? "https://images.unsplash.com/photo-1540910419892-4a36d2c3266c?w=300&h=200&fit=crop"
                        : post.title.includes("Policy Development")
                        ? "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=200&fit=crop"
                        : post.title.includes("Political Communication")
                        ? "https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=300&h=200&fit=crop"
                        : post.title.includes("Youth Leadership")
                        ? "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=300&h=200&fit=crop"
                        : "https://images.unsplash.com/photo-1552664730-d307ca884978?w=300&h=200&fit=crop"
                    }
                    alt={post.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>

                <div className="flex flex-wrap justify-center gap-2 mb-4">
                  {post.tags.slice(0, 2).map((tag, index) => (
                    <span key={index} className="bg-primary-100 text-primary-800 px-2 py-1 rounded-full text-xs">
                      {tag}
                    </span>
                  ))}
                </div>

                <h3 className="text-lg font-bold text-secondary-900 mb-3 line-clamp-2 leading-tight">
                  {post.title}
                </h3>

                <p className="text-secondary-600 mb-4 text-sm leading-relaxed line-clamp-3">
                  {post.excerpt}
                </p>

                <div className="flex items-center justify-between text-xs text-secondary-500 mb-4">
                  <span>{post.author}</span>
                  <span>{post.readTime}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-secondary-400 text-xs">
                    {new Date(post.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                  <button className="text-primary-600 font-semibold hover:text-primary-700 transition-colors duration-200 text-sm">
                    Read →
                  </button>
                </div>
              </article>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center space-x-2">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>

              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => paginate(index + 1)}
                  className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                    currentPage === index + 1
                      ? 'bg-primary-600 text-white shadow-lg'
                      : 'bg-white hover:bg-primary-100'
                  }`}
                >
                  {index + 1}
                </button>
              ))}

              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Signup */}
      <section id="newsletter" className="bg-gradient-to-r from-primary-600 to-primary-800 text-white section-padding">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Stay Ahead of Political Trends
            </h2>
            <p className="text-xl mb-8 text-primary-100">
              Get exclusive access to campaign intelligence, strategic insights, and political market analysis
              delivered directly to your inbox. Join 2,000+ political professionals who rely on our weekly briefing.
            </p>

            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="text-center">
                  <div className="text-4xl mb-4 text-primary-600">
                    <i className="bi-envelope"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Weekly Intelligence Brief</h3>
                  <p className="text-primary-100">Strategic insights and campaign analysis every Monday</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl mb-4 text-primary-600">
                    <i className="bi-bullseye"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Exclusive Research</h3>
                  <p className="text-primary-100">Access to proprietary polling data and voter analysis</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl mb-4 text-primary-600">
                    <i className="bi-lightbulb"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Strategic Alerts</h3>
                  <p className="text-primary-100">Real-time notifications on political developments</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl mb-4 text-primary-600">
                    <i className="bi-book"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Resource Library</h3>
                  <p className="text-primary-100">Download exclusive campaign playbooks and templates</p>
                </div>
              </div>

              <form className="max-w-md mx-auto">
                <div className="flex gap-4">
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    className="flex-1 px-4 py-3 rounded-lg text-secondary-900 focus:outline-none focus:ring-2 focus:ring-white"
                  />
                  <button
                    type="submit"
                    className="btn-primary bg-white text-primary-600 hover:bg-secondary-50 px-8"
                  >
                    Subscribe
                  </button>
                </div>
              </form>

              <p className="text-primary-200 text-sm mt-4">
                Join political leaders, campaign managers, and strategists. Unsubscribe anytime.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">2,000+</div>
                <div className="text-primary-200 text-sm">Subscribers</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">150+</div>
                <div className="text-primary-200 text-sm">Weekly Reports</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">95%</div>
                <div className="text-primary-200 text-sm">Satisfaction</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">24/7</div>
                <div className="text-primary-200 text-sm">Intelligence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Content */}
      <section className="bg-white section-padding">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-4">
              Explore Our Services
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              Put these insights into action with our comprehensive campaign services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center bg-gradient-to-br from-primary-50 to-primary-100 p-8 rounded-2xl hover:shadow-xl transition-shadow duration-300">
              <div className="text-6xl mb-4 text-primary-600">
                <i className="bi-bullseye"></i>
              </div>
              <h3 className="text-xl font-bold text-secondary-900 mb-3">Campaign Strategy</h3>
              <p className="text-secondary-600 mb-6">Turn insights into winning strategies</p>
              <Link to="/services" className="btn-primary">Learn More</Link>
            </div>

            <div className="text-center bg-gradient-to-br from-secondary-50 to-accent-50 p-8 rounded-2xl hover:shadow-xl transition-shadow duration-300">
              <div className="text-6xl mb-4 text-primary-600">
                <i className="bi-bar-chart-line"></i>
              </div>
              <h3 className="text-xl font-bold text-secondary-900 mb-3">Data Analytics</h3>
              <p className="text-secondary-600 mb-6">Advanced voter analysis and insights</p>
              <Link to="/services" className="btn-primary">Learn More</Link>
            </div>

            <div className="text-center bg-gradient-to-br from-green-50 to-primary-50 p-8 rounded-2xl hover:shadow-xl transition-shadow duration-300">
              <div className="text-6xl mb-4 text-primary-600">
                <i className="bi-phone"></i>
              </div>
              <h3 className="text-xl font-bold text-secondary-900 mb-3">Digital Campaigns</h3>
              <p className="text-secondary-600 mb-6">Modern digital strategy implementation</p>
              <Link to="/services" className="btn-primary">Learn More</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;